import React from 'react';
import { useRouter } from 'next/router';
import { Hydrate, QueryClientProvider } from 'react-query';
import { AppProps } from 'next/app';
import { ToastProvider } from '@components/common/toast';
import { getStores, Stores } from '@stores/index';
import AppContextComponent from '@components/AppContextComponent';
import { LoginModalProvider } from '@components/loginModal/LoginModalContext';
import { StoreProvider } from '@utils/storeProvider';
import '@styles/base.scss';
import '@styles/common.scss';
import { init } from '@utils/sentry';
import { queryClient } from '@utils/queryClient';
import useUrlSessionStorage from '@utils/hooks/useUrlSessionStorage';
import useOnMount from '@utils/hooks/useOnMount';
import useViewerHandle from '@utils/hooks/useViewerHandle';
import AuthUtil from '@utils/AuthUtil';

init();

const RootProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <ToastProvider>
      <LoginModalProvider>{children}</LoginModalProvider>
    </ToastProvider>
  );
};

const GlobalRouter = () => {
  const { asPath } = useRouter();
  useUrlSessionStorage(asPath);

  return null;
};

type CustomAppProps = AppProps & {
  err?: Error;
};

const rootStores: Stores = getStores();

const CustomApp = ({ Component, pageProps, err }: CustomAppProps) => {
  useOnMount(() => {
    AuthUtil.checkAuthorization();
  });

  useOnMount(() => {
    document.body.classList.add('scrollbar');

    return () => {
      document.body.classList.remove('scrollbar');
    };
  });

  useViewerHandle();

  return (
    <QueryClientProvider client={queryClient}>
      <Hydrate state={pageProps.dehydratedState}>
        <StoreProvider value={rootStores}>
          <RootProvider>
            <Component {...pageProps} err={err} />
            {/* 전역에서 사용되는 컴포넌트 */}
            <AppContextComponent />
          </RootProvider>
        </StoreProvider>
      </Hydrate>
      <GlobalRouter />
      {/*<ReactQueryDevtools initialIsOpen />*/}
    </QueryClientProvider>
  );
};

export default CustomApp;
